import { getAuth } from "firebase/auth";
import {
  addDoc,
  collection,
  onSnapshot,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { useEffect, useState } from "react";

const CommentModal = ({ task, onClose, onCommentAdded, db }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [currentUserName, setCurrentUserName] = useState("Anonymous User");
  const [isSubmitting, setIsSubmitting] = useState(false); // New loading state for submission
  const [auth, setAuth] = useState();
  useEffect(() => {
    const auth = getAuth();
    setAuth(auth);
    if (auth && auth.currentUser) {
      setCurrentUserName(
        auth.currentUser.displayName ||
          auth.currentUser.email ||
          "Anonymous User"
      );
    }
  }, []);

  useEffect(() => {
    if (!db || !task || !task.id) return;

    const commentsRef = collection(
      db,
      `kanban_tasks/${task.id}/comments`
    );
    const q = query(commentsRef);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const fetchedComments = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setComments(
          fetchedComments.sort(
            (a, b) =>
              (a.createdAt?.toDate() || 0) - (b.createdAt?.toDate() || 0)
          )
        );
      },
      (error) => {
        console.error("Error fetching comments:", error);
      }
    );

    return () => unsubscribe();
  }, [task.id, db]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (
      !newComment.trim() ||
      !db ||
      !task ||
      !task.id ||
      !auth ||
      !auth.currentUser
    ) {
      console.warn("Cannot add empty comment or Firebase/Auth not ready.");
      return;
    }

    setIsSubmitting(true); // Set loading state
    try {
      const commentsCollectionRef = collection(
        db,
        `kanban_tasks/${task.id}/comments`
      );
      await addDoc(commentsCollectionRef, {
        text: newComment,
        createdAt: serverTimestamp(),
        userId: auth.currentUser.uid,
        userName: currentUserName,
      });
      setNewComment("");
      onCommentAdded(task.id);
      console.log("Comment added successfully!");
    } catch (error) {
      console.error("Error adding comment:", error);
      // Optionally show user a message about the error
    } finally {
      setIsSubmitting(false); // Reset loading state
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-xl shadow-xl relative text-white flex flex-col max-h-[90vh]">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-white p-2 rounded-full hover:bg-gray-700 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <h2 className="text-2xl font-bold mb-4">Comments for "{task.title}"</h2>

        {/* Existing Comments Section */}
        <div className="flex-grow overflow-y-auto custom-scrollbar p-2 mb-4 rounded-md bg-gray-700">
          {comments.length === 0 ? (
            <p className="text-gray-400 text-center py-4">
              No comments yet. Be the first to comment!
            </p>
          ) : (
            comments.map((comment) => (
              <div
                key={comment.id}
                className="bg-gray-900 rounded-md p-3 mb-2 shadow-sm"
              >
                <div className="flex justify-between items-center text-sm mb-1">
                  <span className="font-semibold text-blue-300">
                    {comment.userName || "Unknown User"}
                  </span>
                  <span className="text-gray-400 text-xs">
                    {comment.createdAt?.toDate
                      ? new Date(comment.createdAt.toDate()).toLocaleString()
                      : "Just now"}
                  </span>
                </div>
                <p className="text-gray-200 text-sm">{comment.text}</p>
              </div>
            ))
          )}
        </div>

        {/* Add New Comment Form */}
        <form onSubmit={handleAddComment} className="flex flex-col space-y-3">
          <textarea
            className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 outline-none text-white resize-none"
            rows="3"
            placeholder="Add a new comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            required
            disabled={isSubmitting} // Disable while submitting
          ></textarea>
          <button
            type="submit"
            className="self-end px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSubmitting} // Disable while submitting
          >
            {isSubmitting ? "Adding..." : "Add Comment"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CommentModal